// src/pages/WorkspaceSettings.jsx
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { API_BASE, getCsrfToken } from "../services/server";
import { useAuth } from "../services/AuthContext";
import Main from "./Main";
import NoAccess from "./components/NoAccess";
import { makeCompanyNav } from "./nav";
import "./css/settings.css";
import {
  SettingsShell,
  SettingsSidebarCard,
  SettingsHeader,
  SettingsCard,
  SettingsField,
  SettingsEmptyState,
} from "./components/settingsComponents";
import useWorkspaceLifecycle from "../hooks/useWorkspaceLifecycle";
import { CloseWorkspaceButton, WorkspaceStatusBadge } from "./components/WorkspaceLifecycleUI";


const NAV_ITEMS = [
  { href: "#general",       label: "General" },
  { href: "#brokers",       label: "Brokers" },
  { href: "#nda",           label: "NDA" },
  { href: "#loi",           label: "LOI" },
  { href: "#invite",        label: "Invite seller" },
  // Köpar-inbjudningar görs nu enbart via Pipeline-vyn, inte här.
  { href: "#account",       label: "Account" },
  { href: "#danger",        label: "Danger zone" },
];

export default function WorkspaceSettings() {
  const { company: workspaceKey = "" } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const currentEmail = user?.user?.email || user?.user?.username || user?.email || user?.username || "—";

  // Lifecycle: behövs för Danger zone-knappen "Stäng affär" — den
  // visas bara när workspace är ACTIVE/ARCHIVED och brokern har
  // can_manage_lifecycle. Reload triggas efter close så banner +
  // status badge uppdateras direkt utan refresh.
  const { workspace: wsLifecycle, reload: reloadLifecycle } = useWorkspaceLifecycle(workspaceKey);

  const [loggingOut, setLoggingOut] = useState(false);

  async function handleLogout() {
    if (loggingOut) return;
    setLoggingOut(true);
    try {
      await logout();
      navigate("/login");
    } catch {
      setLoggingOut(false);
    }
  }

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const [workspaceName, setWorkspaceName] = useState("");
  const [workspaceStatus, setWorkspaceStatus] = useState("active");

  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteCompanyName, setInviteCompanyName] = useState("");
  const [inviteModules, setInviteModules] = useState({ financials: false, dd: false, company_details: false });
  const [inviting, setInviting] = useState(false);
  const [inviteMessage, setInviteMessage] = useState("");
  const [inviteError, setInviteError] = useState("");

  const [buyerEmail, setBuyerEmail] = useState("");
  const [buyerCompanyName, setBuyerCompanyName] = useState("");
  const [buyerModules, setBuyerModules] = useState({ valuation: false, dataroom: false, dd: false, im: false });
  const [buyerInviting, setBuyerInviting] = useState(false);
  const [buyerMessage, setBuyerMessage] = useState("");
  const [buyerError, setBuyerError] = useState("");

  // Broker users (colleagues from the same broker company)
  const [brokerCompanyId, setBrokerCompanyId] = useState(null);
  const [brokerColleagues, setBrokerColleagues] = useState([]);     // full list from /companies/me/members/
  const [workspaceBrokerUsers, setWorkspaceBrokerUsers] = useState([]); // those already in the workspace
  const [brokerBusy, setBrokerBusy] = useState({}); // { user_id: bool }
  // Per-row role selection for the "add" form: { user_id: "admin" | "member" }
  const [brokerAddRole, setBrokerAddRole] = useState({});
  const [brokerMessage, setBrokerMessage] = useState("");
  const [brokerError, setBrokerError] = useState("");

  useEffect(() => {
    if (!workspaceKey) {
      setError("No workspace key found.");
      setLoading(false);
      return;
    }
    loadWorkspace();
    loadBrokerUsers();
  }, [workspaceKey]);

  async function loadWorkspace() {
    setLoading(true);
    setError("");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE}/users/workspace/${workspaceKey}/`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not load workspace.");
      setWorkspaceName(data.organisation_name || "");
      setWorkspaceStatus(data.deal_status || "active");
      // The workspace's owner_company is the broker firm that runs this
      // deal — trust this over the caller's membership list. This is how
      // we keep sellers from showing up in the Brokers section even when
      // the caller happens to be admin of multiple companies.
      if (data.owner_company_id) {
        setBrokerCompanyId(data.owner_company_id);
      }
    } catch (err) {
      setError(err.message || "Could not load workspace.");
    } finally {
      setLoading(false);
    }
  }

  async function loadBrokerUsers() {
    setBrokerError("");
    setBrokerMessage("");
    try {
      // 1) Workspace users — include side, filter to active + broker-side only.
      //    Server returns each user's workspace-level side ("broker"/"seller"/
      //    "buyer") so we never need to guess from the caller's membership.
      const wsRes = await fetch(
        `${API_BASE}/users/workspaces/${workspaceKey}/users/`,
        { credentials: "include" },
      );
      const wsData = await wsRes.json().catch(() => []);
      let brokerCoIdFromWs = null;
      if (wsRes.ok && Array.isArray(wsData)) {
        const activeBrokers = wsData.filter(
          (u) => u.status === "active" && u.side === "broker",
        );
        setWorkspaceBrokerUsers(activeBrokers);
        brokerCoIdFromWs = activeBrokers[0]?.company_id || null;
      }

      // Prefer broker_company_id from workspace detail (set by loadWorkspace);
      // fall back to the one inferred from the workspace users list.
      const effectiveBrokerCoId = brokerCompanyId || brokerCoIdFromWs;

      // 2) Colleagues — scope the request to the broker company explicitly.
      //    The IsCompanyAdmin permission honours ?company_id=X so we get the
      //    right tenant even if the caller is admin of multiple companies.
      const colleaguesUrl = effectiveBrokerCoId
        ? `${API_BASE}/users/companies/me/members/?company_id=${effectiveBrokerCoId}`
        : `${API_BASE}/users/companies/me/members/`;
      const colleaguesRes = await fetch(colleaguesUrl, { credentials: "include" });
      const colleaguesData = await colleaguesRes.json().catch(() => []);
      if (colleaguesRes.ok && Array.isArray(colleaguesData)) {
        setBrokerColleagues(colleaguesData);
      }
    } catch {
      setBrokerError("Kunde inte ladda användare.");
    }
  }

  async function addBrokerToWorkspace(userId) {
    if (!brokerCompanyId) {
      setBrokerError("Broker-bolag saknas.");
      return;
    }
    const role = brokerAddRole[userId] || "admin";
    if (role !== "admin" && role !== "member") {
      setBrokerError("Ogiltig roll.");
      return;
    }
    setBrokerBusy((b) => ({ ...b, [userId]: true }));
    setBrokerError("");
    setBrokerMessage("");
    const csrf = getCsrfToken();
    try {
      const res = await fetch(
        `${API_BASE}/users/workspaces/${workspaceKey}/users/`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(csrf ? { "X-CSRFToken": csrf } : {}),
          },
          body: JSON.stringify({
            user_id: userId,
            company_id: brokerCompanyId,
            role,
          }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Kunde inte lägga till.");
      setBrokerMessage("Tillagd.");
      setBrokerAddRole((m) => {
        const next = { ...m };
        delete next[userId];
        return next;
      });
      await loadBrokerUsers();
    } catch (err) {
      setBrokerError(err.message);
    } finally {
      setBrokerBusy((b) => ({ ...b, [userId]: false }));
    }
  }

  async function changeBrokerRole(userId, newRole) {
    if (newRole !== "admin" && newRole !== "member") return;
    setBrokerBusy((b) => ({ ...b, [userId]: true }));
    setBrokerError("");
    setBrokerMessage("");
    const csrf = getCsrfToken();
    try {
      const res = await fetch(
        `${API_BASE}/users/workspaces/${workspaceKey}/users/${userId}/`,
        {
          method: "PATCH",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(csrf ? { "X-CSRFToken": csrf } : {}),
          },
          body: JSON.stringify({ role: newRole }),
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Kunde inte uppdatera roll.");
      setBrokerMessage("Roll uppdaterad.");
      await loadBrokerUsers();
    } catch (err) {
      setBrokerError(err.message);
    } finally {
      setBrokerBusy((b) => ({ ...b, [userId]: false }));
    }
  }

  async function removeBrokerFromWorkspace(userId) {
    setBrokerBusy((b) => ({ ...b, [userId]: true }));
    setBrokerError("");
    setBrokerMessage("");
    const csrf = getCsrfToken();
    try {
      const res = await fetch(
        `${API_BASE}/users/workspaces/${workspaceKey}/users/${userId}/`,
        {
          method: "DELETE",
          credentials: "include",
          headers: { ...(csrf ? { "X-CSRFToken": csrf } : {}) },
        },
      );
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Kunde inte ta bort.");
      }
      setBrokerMessage("Borttagen.");
      await loadBrokerUsers();
    } catch (err) {
      setBrokerError(err.message);
    } finally {
      setBrokerBusy((b) => ({ ...b, [userId]: false }));
    }
  }

  async function saveWorkspace() {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE}/users/workspace/${workspaceKey}/`, {
        method: "PATCH",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deal_status: workspaceStatus }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not save workspace.");
      setWorkspaceStatus(data.deal_status || workspaceStatus);
      setMessage("Sparat.");
    } catch (err) {
      setError(err.message || "Could not save workspace.");
    } finally {
      setSaving(false);
    }
  }

  async function deactivateWorkspace() {
    setError("");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE}/users/workspace/${workspaceKey}/`, {
        method: "DELETE",
        credentials: "include",
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not deactivate workspace.");
      setWorkspaceStatus("inactive");
      setMessage("Workspace inaktiverat.");
    } catch (err) {
      setError(err.message || "Could not deactivate workspace.");
    }
  }

  async function deleteWorkspace() {
    if (!window.confirm("Är du säker på att du vill ta bort detta workspace permanent?")) return;
    setError("");
    setMessage("");
    try {
      const res = await fetch(`${API_BASE}/users/workspace/${workspaceKey}/?hard=true`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || "Could not delete workspace.");
      }
      window.location.href = "/projects/";
    } catch (err) {
      setError(err.message || "Could not delete workspace.");
    }
  }

  async function sendBuyerInvite() {
    const email = buyerEmail.trim().toLowerCase();
    const companyName = buyerCompanyName.trim();
    const modules = Object.entries(buyerModules).filter(([, v]) => v).map(([k]) => k);
    if (!email || !companyName || modules.length === 0) return;

    setBuyerInviting(true);
    setBuyerError("");
    setBuyerMessage("");
    const csrf = getCsrfToken();
    try {
      const res = await fetch(`${API_BASE}/users/buyer-invitations/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRFToken": csrf } : {}),
        },
        body: JSON.stringify({ workspace_key: workspaceKey, email, buyer_company_name: companyName, modules }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not send invitation.");
      setBuyerEmail("");
      setBuyerCompanyName("");
      setBuyerModules({ valuation: false, dataroom: false, dd: false, im: false });
      setBuyerMessage(`Inbjudan skickad till ${email}.`);
    } catch (err) {
      setBuyerError(err.message);
    } finally {
      setBuyerInviting(false);
    }
  }

  async function sendSellerInvite() {
    const email = inviteEmail.trim().toLowerCase();
    const companyName = inviteCompanyName.trim();
    const modules = Object.entries(inviteModules).filter(([, v]) => v).map(([k]) => k);
    if (!email || !companyName || modules.length === 0) return;

    setInviting(true);
    setInviteError("");
    setInviteMessage("");
    const csrf = getCsrfToken();
    try {
      const res = await fetch(`${API_BASE}/users/workspace-invitations/`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(csrf ? { "X-CSRFToken": csrf } : {}),
        },
        body: JSON.stringify({ workspace_key: workspaceKey, email, seller_company_name: companyName, modules }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || "Could not send invitation.");
      setInviteEmail("");
      setInviteCompanyName("");
      setInviteModules({ financials: false, dd: false, company_details: false });
      setInviteMessage(`Inbjudan skickad till ${email}.`);
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setInviting(false);
    }
  }

  const sidebar = (
    <SettingsSidebarCard title="Inställningar">
      <nav className="st-nav">
        {NAV_ITEMS.map((item) => (
          <a key={item.href} href={item.href} className="st-nav-item">
            {item.label}
          </a>
        ))}
      </nav>
    </SettingsSidebarCard>
  );

  // Sellers and buyers never see the workspace settings page — it exposes
  // invite forms, the NDA template editor, and the danger zone. Block them
  // at the outer boundary so nothing below can accidentally leak through.
  const isBroker = Boolean(user?.global_nav?.is_broker);
  if (user && !isBroker) {
    return (
      <Main navItems={makeCompanyNav(workspaceKey)}>
        <NoAccess message="Workspace-inställningarna är endast tillgängliga för mäklare." />
      </Main>
    );
  }

  if (loading) {
    return (
      <Main navItems={makeCompanyNav(workspaceKey)}>
        <SettingsShell sidebar={sidebar}>
          <SettingsEmptyState>Laddar…</SettingsEmptyState>
        </SettingsShell>
      </Main>
    );
  }

  return (
    <Main navItems={makeCompanyNav(workspaceKey)}>
      <SettingsShell sidebar={sidebar}>
        <SettingsHeader
          title="Workspace-inställningar"
          subtitle="Hantera detta workspace och bjud in säljare."
          action={
            <button
              className="st-btn st-btn--primary"
              onClick={saveWorkspace}
              disabled={saving}
            >
              {saving ? "Sparar…" : "Spara"}
            </button>
          }
        />

        {error && <div className="st-error">{error}</div>}
        {message && <p className="st-success">{message}</p>}

        {/* General */}
        <SettingsCard id="general" title="Allmänt" subtitle="Grundläggande information om detta workspace.">
          <div className="st-grid">
            <SettingsField label="Workspace-namn">
              <input type="text" value={workspaceName} readOnly />
            </SettingsField>
            <SettingsField label="Status">
              <select value={workspaceStatus} onChange={(e) => setWorkspaceStatus(e.target.value)}>
                <option value="active">Aktiv</option>
                <option value="pending">Väntande</option>
                <option value="inactive">Inaktiv</option>
              </select>
            </SettingsField>
          </div>
        </SettingsCard>

        {/* Brokers */}
        <SettingsCard
          id="brokers"
          title="Mäklare"
          subtitle="Lägg till kollegor från ditt mäklarbolag till detta workspace. Alla får admin-rättigheter."
        >
          {brokerMessage && <p className="st-success">{brokerMessage}</p>}
          {brokerError && <p style={{ color: "#dc2626", fontSize: 13 }}>{brokerError}</p>}

          {(() => {
            const inWorkspaceIds = new Set(workspaceBrokerUsers.map((u) => u.user_id));
            const notInWorkspace = brokerColleagues.filter(
              (c) => !inWorkspaceIds.has(c.user_id) && c.is_active,
            );

            return (
              <>
                <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginBottom: 6 }}>
                  I detta workspace
                </div>
                {workspaceBrokerUsers.length === 0 && (
                  <p style={{ color: "#9ca3af", fontSize: 13, marginBottom: 14 }}>
                    Inga mäklare tillagda ännu.
                  </p>
                )}
                {workspaceBrokerUsers.map((u) => (
                  <div key={u.user_id} className="st-danger-row" style={{ marginBottom: 8 }}>
                    <div>
                      <div className="st-row-title">{u.email || u.username}</div>
                      <div className="st-row-desc">{u.status}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <select
                        value={u.role}
                        disabled={brokerBusy[u.user_id]}
                        onChange={(e) => changeBrokerRole(u.user_id, e.target.value)}
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                      </select>
                      <button
                        className="st-btn st-btn--ghost-danger"
                        disabled={brokerBusy[u.user_id]}
                        onClick={() => removeBrokerFromWorkspace(u.user_id)}
                      >
                        {brokerBusy[u.user_id] ? "…" : "Ta bort"}
                      </button>
                    </div>
                  </div>
                ))}

                <div style={{ fontSize: 12, fontWeight: 600, color: "#6b7280", marginTop: 18, marginBottom: 6 }}>
                  Tillgängliga kollegor
                </div>
                {notInWorkspace.length === 0 && (
                  <p style={{ color: "#9ca3af", fontSize: 13 }}>
                    Alla aktiva kollegor i ditt bolag är redan tillagda.
                  </p>
                )}
                {notInWorkspace.map((c) => (
                  <div key={c.user_id} className="st-danger-row" style={{ marginBottom: 8 }}>
                    <div>
                      <div className="st-row-title">{c.email || c.username}</div>
                      <div className="st-row-desc">Bolagsroll: {c.role}</div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <select
                        value={brokerAddRole[c.user_id] || "admin"}
                        disabled={brokerBusy[c.user_id]}
                        onChange={(e) =>
                          setBrokerAddRole((m) => ({ ...m, [c.user_id]: e.target.value }))
                        }
                      >
                        <option value="admin">Admin</option>
                        <option value="member">Member</option>
                      </select>
                      <button
                        className="st-btn st-btn--primary"
                        disabled={brokerBusy[c.user_id]}
                        onClick={() => addBrokerToWorkspace(c.user_id)}
                      >
                        {brokerBusy[c.user_id] ? "Lägger till…" : "Lägg till"}
                      </button>
                    </div>
                  </div>
                ))}
              </>
            );
          })()}
        </SettingsCard>

        {/* NDA */}
        <SettingsCard id="nda" title="Sekretessavtal (NDA)" subtitle="Redigera NDA-mallen och styr om säljare måste signera innan de får tillgång.">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              NDA-mallen kan anpassas med er logotyp, fritext och egna taggar.
              Säljare presenteras med dokumentet och måste signera digitalt innan de når portalen.
            </div>
            <Link
              to={`/nda-editor/${workspaceKey}`}
              className="st-btn st-btn--secondary"
              style={{ textDecoration: "none", whiteSpace: "nowrap" }}
            >
              Redigera NDA-mall →
            </Link>
          </div>
        </SettingsCard>

        {/* LOI */}
        <SettingsCard id="loi" title="Avsiktsförklaring (LOI)" subtitle="Redigera LOI-mallen som skickas till köpare från pipelinen.">
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
            <div style={{ fontSize: 13, color: "#6b7280" }}>
              LOI-mallen kan anpassas med fritext och egna taggar (t.ex. budbelopp, exklusivitetsdagar).
              Mallen skickas per köpare från pipelinen och signeras digitalt via samma flöde som NDA.
            </div>
            <Link
              to={`/loi-editor/${workspaceKey}`}
              className="st-btn st-btn--secondary"
              style={{ textDecoration: "none", whiteSpace: "nowrap" }}
            >
              Redigera LOI-mall →
            </Link>
          </div>
        </SettingsCard>

        {/* Invite seller */}
        <SettingsCard id="invite" title="Bjud in säljare" subtitle="Skicka en e-postinbjudan till en säljare med åtkomst till valda moduler.">
          <div className="st-grid" style={{ marginBottom: 14 }}>
            <SettingsField label="Säljarens e-post">
              <input type="email" placeholder="seller@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
            </SettingsField>
            <SettingsField label="Säljarens bolagsnamn">
              <input type="text" placeholder="Acme AB" value={inviteCompanyName} onChange={(e) => setInviteCompanyName(e.target.value)} />
            </SettingsField>
          </div>
          <SettingsField label="Moduler">
            <div className="st-stack" style={{ marginTop: 4 }}>
              {[
                { key: "financials",      label: "Finansiell data (SIE / årsredovisningar)" },
                { key: "dd",              label: "Due diligence-frågeformulär" },
                { key: "company_details", label: "Bolagsinfo (logotyp, beskrivning, team)" },
              ].map(({ key, label }) => (
                <label key={key} className="st-check-row">
                  <input
                    type="checkbox"
                    checked={inviteModules[key]}
                    onChange={() => setInviteModules((m) => ({ ...m, [key]: !m[key] }))}
                  />
                  <span>{label}</span>
                </label>
              ))}
            </div>
          </SettingsField>
          {inviteMessage && <p className="st-success" style={{ marginTop: 10 }}>{inviteMessage}</p>}
          {inviteError && <p style={{ color: "#dc2626", fontSize: 13, marginTop: 10 }}>{inviteError}</p>}
          <div style={{ marginTop: 16 }}>
            <button
              className="st-btn st-btn--primary"
              onClick={sendSellerInvite}
              disabled={inviting || !inviteEmail.trim() || !inviteCompanyName.trim() || !Object.values(inviteModules).some(Boolean)}
            >
              {inviting ? "Skickar…" : "Skicka inbjudan"}
            </button>
          </div>
        </SettingsCard>

        {/* Köpar-inbjudningar tidigare här — flyttat till Pipeline-vyn,
            där broker har full kontext kring vilken köpare som ska få vad. */}

        {/* Account */}
        <SettingsCard id="account" title="Konto" subtitle="Ditt Qada-konto.">
          <div className="st-danger-row" style={{ marginBottom: 8 }}>
            <div>
              <div className="st-row-title">Inloggad som</div>
              <div className="st-row-desc">{currentEmail}</div>
            </div>
          </div>
          <div className="st-danger-row">
            <div>
              <div className="st-row-title">Logga ut</div>
              <div className="st-row-desc">Avsluta sessionen och gå tillbaka till inloggningssidan.</div>
            </div>
            <button
              className="st-btn st-btn--secondary"
              onClick={handleLogout}
              disabled={loggingOut}
            >
              {loggingOut ? "Loggar ut…" : "Logga ut"}
            </button>
          </div>
        </SettingsCard>

        {/* Danger zone */}
        <SettingsCard id="danger" title="Danger zone" subtitle="Åtgärder med stor påverkan för detta workspace." danger>
          {/* Stäng affär — flippar till CLOSING + räknar ned till
              CLOSED. Stripe-prenumerationen sägs upp till samma datum
              så brokern inte debiteras efter stängning. Köpare/säljare
              sparkas ut vid finaliseringen; workspacet behålls för
              revision men låses permanent från ändringar. */}
          {wsLifecycle?.can_manage_lifecycle && (
            <div className="st-danger-row" style={{ alignItems: "flex-start" }}>
              <div>
                <div className="st-row-title" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  Stäng affär
                  {wsLifecycle?.deal_status && (
                    <WorkspaceStatusBadge status={wsLifecycle.deal_status} />
                  )}
                </div>
                <div className="st-row-desc">
                  Stänger affären och säger upp prenumerationen för detta
                  workspace. Köpare och säljare sparkas ut, workspacet låses
                  permanent från ändringar men behålls för revision (~7 dagar
                  eller månadens slut). Går inte att ångra efter att
                  nedstängningen finaliserats.
                </div>
              </div>
              <CloseWorkspaceButton workspace={wsLifecycle} onClosed={reloadLifecycle} />
            </div>
          )}
          <div className="st-danger-row">
            <div>
              <div className="st-row-title">Inaktivera workspace</div>
              <div className="st-row-desc">Behåll workspacet men gör det inaktivt.</div>
            </div>
            <button className="st-btn st-btn--ghost-danger" onClick={deactivateWorkspace}>
              Inaktivera
            </button>
          </div>
          <div className="st-danger-row">
            <div>
              <div className="st-row-title">Ta bort workspace</div>
              <div className="st-row-desc">Ta bort workspacet och allt relaterat innehåll permanent.</div>
            </div>
            <button className="st-btn st-btn--danger" onClick={deleteWorkspace}>
              Ta bort
            </button>
          </div>
        </SettingsCard>
      </SettingsShell>
    </Main>
  );
}
